/**
 * TransitsPage — Consultation des transits importés
 *
 * Tableau chronologique filtrable des aspects de transit.
 * Filtres : planète, aspect, point natal, période, direction.
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api.js';
import { colors } from '../styles/tokens.js';

/** Aspect de transit (retourné par l'API) */
interface TransitAspect {
  date: string;
  heure: string;
  planeteTransit: string;
  aspect: string;
  pointNatal: string;
  signe: string;
  position: string;
  vitesse: number | null;
  direction: string;
  planeteTransitSymbole: string;
  pointNatalSymbole: string;
}

interface TransitsData {
  metadata: {
    dateDebut: string | null;
    dateFin: string | null;
    nomClient: string | null;
    totalPages: number;
  };
  aspects: TransitAspect[];
  totalAspects: number;
  filteredCount: number;
  stats: {
    total: number;
    parPlanete: Record<string, number>;
    parAspect: Record<string, number>;
  };
  symbolesInconnus: Array<{ type: string; symbole: string }>;
}

/** Couleurs par nature d'aspect */
const ASPECT_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  Conjonction: { bg: '#fefce8', text: '#854d0e', border: '#fde68a' },
  Opposition: { bg: '#fef2f2', text: '#991b1b', border: '#fca5a5' },
  'Carré': { bg: '#fef2f2', text: '#991b1b', border: '#fca5a5' },
  Trigone: { bg: '#f0fdf4', text: '#166534', border: '#86efac' },
  Sextile: { bg: '#f0fdf4', text: '#166534', border: '#86efac' },
};

/** Symboles Unicode pour affichage */
const PLANET_SYMBOLS: Record<string, string> = {
  Soleil: '☉', Lune: '☽', Mercure: '☿', 'Vénus': '♀', Mars: '♂',
  Jupiter: '♃', Saturne: '♄', Uranus: '♅', Neptune: '♆', Pluton: '♇',
  Chiron: '⚷', 'Noeud Nord': '☊', 'Noeud Sud': '☋', 'Lune Noire': '⚸',
  Ascendant: 'AS', 'Milieu du Ciel': 'MC', Descendant: 'DS', 'Fond du Ciel': 'FC',
};

const ASPECT_SYMBOLS: Record<string, string> = {
  Conjonction: '☌', Opposition: '☍', 'Carré': '□', Trigone: '△', Sextile: '✶',
};

/** Planètes lentes (transit significatif) */
const PLANETES_LENTES = ['Jupiter', 'Saturne', 'Uranus', 'Neptune', 'Pluton', 'Chiron'];

export function TransitsPage() {
  const { id, chartId } = useParams<{ id: string; chartId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<TransitsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Filtres
  const [filterPlanetes, setFilterPlanetes] = useState<Set<string>>(new Set());
  const [filterAspects, setFilterAspects] = useState<Set<string>>(new Set());
  const [filterPointNatal, setFilterPointNatal] = useState('');
  const [exclureLune, setExclureLune] = useState(true);
  const [filterDirection, setFilterDirection] = useState('');

  // Vue
  const [viewMode, setViewMode] = useState<'chronologique' | 'parPlanete'>('chronologique');

  // Pagination
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  useEffect(() => {
    if (!id || !chartId) return;
    setIsLoading(true);
    setError('');

    const params = new URLSearchParams();
    if (exclureLune) params.set('exclureLune', 'true');

    apiFetch<TransitsData>(`/clients/${id}/charts/${chartId}/transits?${params.toString()}`)
      .then((res) => {
        if (res.success && res.data) {
          setData(res.data);
        } else {
          setError(res.error ?? 'Erreur de chargement');
        }
      })
      .catch(() => setError('Erreur de connexion'))
      .finally(() => setIsLoading(false));
  }, [id, chartId, exclureLune]);

  // Appliquer les filtres locaux
  const filteredAspects = useMemo(() => {
    if (!data) return [];
    let aspects = data.aspects;

    if (filterPlanetes.size > 0) {
      aspects = aspects.filter((a) => filterPlanetes.has(a.planeteTransit));
    }
    if (filterAspects.size > 0) {
      aspects = aspects.filter((a) => filterAspects.has(a.aspect));
    }
    if (filterPointNatal) {
      aspects = aspects.filter((a) => a.pointNatal === filterPointNatal);
    }
    if (filterDirection) {
      aspects = aspects.filter((a) => a.direction === filterDirection);
    }

    return aspects;
  }, [data, filterPlanetes, filterAspects, filterPointNatal, filterDirection]);

  // Reset page quand les filtres changent
  useEffect(() => {
    setPage(1);
  }, [filterPlanetes, filterAspects, filterPointNatal, filterDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAspects.length / PAGE_SIZE);
  const paginatedAspects = filteredAspects.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Grouper par planète pour la vue par planète
  const groupedByPlanete = useMemo(() => {
    const groups: Record<string, TransitAspect[]> = {};
    for (const a of filteredAspects) {
      if (!groups[a.planeteTransit]) groups[a.planeteTransit] = [];
      groups[a.planeteTransit]!.push(a);
    }
    return groups;
  }, [filteredAspects]);

  // Listes pour les filtres
  const allPlanetes = useMemo(() => {
    if (!data) return [];
    return Object.keys(data.stats.parPlanete).sort();
  }, [data]);

  const allPointsNataux = useMemo(() => {
    if (!data) return [];
    const points = new Set(data.aspects.map((a) => a.pointNatal));
    return Array.from(points).sort();
  }, [data]);

  function toggleFilter(set: Set<string>, value: string, setter: (s: Set<string>) => void) {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setter(next);
  }

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: colors.textMuted }}>
        Chargement des transits...
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{
        background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
        padding: '10px 14px', fontSize: 14, color: '#991b1b',
      }}>
        <i className="bi bi-exclamation-circle" style={{ marginRight: 6 }} />
        {error || 'Données introuvables'}
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: colors.text, margin: 0 }}>
          <i className="bi bi-arrow-repeat" style={{ color: '#0891b2', marginRight: 8 }} />
          Transits
          {data.metadata.dateDebut && data.metadata.dateFin && (
            <span style={{ fontSize: '0.85rem', fontWeight: 400, color: colors.textMuted, marginLeft: 12 }}>
              {data.metadata.dateDebut} → {data.metadata.dateFin}
            </span>
          )}
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: '0.85rem', color: colors.textMuted }}>
            {filteredAspects.length} / {data.totalAspects} aspects
          </span>
          <button
            onClick={() => navigate(`/clients/${id}/analyse-transit-ia/${chartId}`)}
            style={{
              background: 'linear-gradient(135deg, #0891b2, #06b6d4)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '8px 16px',
              fontSize: '0.85rem',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            ✨ Générer analyse transits
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div style={{
        background: '#fff', borderRadius: 8, padding: '1rem 1.25rem', marginBottom: '1rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          {/* Toggle Lune */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={exclureLune}
              onChange={(e) => setExclureLune(e.target.checked)}
              style={{ accentColor: '#0891b2' }}
            />
            Masquer la Lune
          </label>

          <span style={{ color: colors.border }}>|</span>

          {/* Planètes lentes */}
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {allPlanetes.filter((p) => p !== 'Lune' || !exclureLune).map((planete) => (
              <button
                key={planete}
                onClick={() => toggleFilter(filterPlanetes, planete, setFilterPlanetes)}
                style={{
                  padding: '3px 10px', borderRadius: 16, fontSize: '0.75rem', fontWeight: 500,
                  cursor: 'pointer', border: '1px solid',
                  borderColor: filterPlanetes.has(planete) ? '#0891b2' : colors.border,
                  background: filterPlanetes.has(planete) ? '#ecfeff' : '#fff',
                  color: filterPlanetes.has(planete) ? '#0891b2' : colors.textMid,
                }}
              >
                {PLANET_SYMBOLS[planete] ?? ''} {planete}
              </button>
            ))}
          </div>

          <span style={{ color: colors.border }}>|</span>

          {/* Aspects */}
          <div style={{ display: 'flex', gap: 4 }}>
            {['Conjonction', 'Opposition', 'Carré', 'Trigone', 'Sextile'].map((aspect) => (
              <button
                key={aspect}
                onClick={() => toggleFilter(filterAspects, aspect, setFilterAspects)}
                style={{
                  padding: '3px 10px', borderRadius: 16, fontSize: '0.75rem', fontWeight: 500,
                  cursor: 'pointer', border: '1px solid',
                  borderColor: filterAspects.has(aspect)
                    ? (ASPECT_COLORS[aspect]?.border ?? colors.border)
                    : colors.border,
                  background: filterAspects.has(aspect)
                    ? (ASPECT_COLORS[aspect]?.bg ?? '#fff')
                    : '#fff',
                  color: filterAspects.has(aspect)
                    ? (ASPECT_COLORS[aspect]?.text ?? colors.textMid)
                    : colors.textMid,
                }}
              >
                {ASPECT_SYMBOLS[aspect] ?? ''} {aspect}
              </button>
            ))}
          </div>
        </div>

        {/* Filtres secondaires */}
        <div style={{ display: 'flex', gap: '1rem', marginTop: '0.75rem', alignItems: 'center' }}>
          <select
            value={filterPointNatal}
            onChange={(e) => setFilterPointNatal(e.target.value)}
            style={{
              padding: '4px 8px', borderRadius: 6, border: `1px solid ${colors.border}`,
              fontSize: '0.8rem', color: colors.text, background: '#fff',
            }}
          >
            <option value="">Tous les points nataux</option>
            {allPointsNataux.map((p) => (
              <option key={p} value={p}>{PLANET_SYMBOLS[p] ?? ''} {p}</option>
            ))}
          </select>

          <select
            value={filterDirection}
            onChange={(e) => setFilterDirection(e.target.value)}
            style={{
              padding: '4px 8px', borderRadius: 6, border: `1px solid ${colors.border}`,
              fontSize: '0.8rem', color: colors.text, background: '#fff',
            }}
          >
            <option value="">Toutes directions</option>
            <option value="applicant">Applicant</option>
            <option value="séparant">Séparant</option>
          </select>

          {/* Toggle vue */}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
            <button
              onClick={() => setViewMode('chronologique')}
              style={{
                padding: '4px 12px', borderRadius: 6, fontSize: '0.8rem', cursor: 'pointer',
                border: `1px solid ${colors.border}`,
                background: viewMode === 'chronologique' ? '#0891b2' : '#fff',
                color: viewMode === 'chronologique' ? '#fff' : colors.textMid,
              }}
            >
              <i className="bi bi-list-ul" style={{ marginRight: 4 }} />
              Timeline
            </button>
            <button
              onClick={() => setViewMode('parPlanete')}
              style={{
                padding: '4px 12px', borderRadius: 6, fontSize: '0.8rem', cursor: 'pointer',
                border: `1px solid ${colors.border}`,
                background: viewMode === 'parPlanete' ? '#0891b2' : '#fff',
                color: viewMode === 'parPlanete' ? '#fff' : colors.textMid,
              }}
            >
              <i className="bi bi-collection" style={{ marginRight: 4 }} />
              Par planète
            </button>
          </div>

          {/* Reset filtres */}
          {(filterPlanetes.size > 0 || filterAspects.size > 0 || filterPointNatal || filterDirection) && (
            <button
              onClick={() => {
                setFilterPlanetes(new Set());
                setFilterAspects(new Set());
                setFilterPointNatal('');
                setFilterDirection('');
              }}
              style={{
                padding: '4px 10px', borderRadius: 6, fontSize: '0.75rem',
                border: 'none', background: colors.bgPage, color: colors.textMid,
                cursor: 'pointer',
              }}
            >
              <i className="bi bi-x" /> Réinitialiser
            </button>
          )}
        </div>
      </div>

      {/* Contenu */}
      {viewMode === 'chronologique' ? (
        <TransitTimeline aspects={paginatedAspects} />
      ) : (
        <TransitByPlanete groups={groupedByPlanete} />
      )}

      {/* Pagination (vue chronologique uniquement) */}
      {viewMode === 'chronologique' && totalPages > 1 && (
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      )}
    </div>
  );
}

/** Vue timeline chronologique */
function TransitTimeline({ aspects }: { aspects: TransitAspect[] }) {
  if (aspects.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: colors.textMuted }}>
        Aucun aspect ne correspond aux filtres sélectionnés.
      </div>
    );
  }

  // Grouper par date
  const byDate: Record<string, TransitAspect[]> = {};
  for (const a of aspects) {
    if (!byDate[a.date]) byDate[a.date] = [];
    byDate[a.date]!.push(a);
  }

  return (
    <div style={{ display: 'grid', gap: '0.5rem' }}>
      {Object.entries(byDate).map(([date, dateAspects]) => (
        <div key={date}>
          {/* Séparateur de date */}
          <div style={{
            fontSize: '0.8rem', fontWeight: 600, color: colors.textMid,
            padding: '0.5rem 0', borderBottom: `1px solid ${colors.border}`, marginBottom: '0.25rem',
          }}>
            {date}
          </div>
          {dateAspects.map((a, i) => (
            <TransitRow key={`${date}-${i}`} aspect={a} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Ligne d'un aspect de transit */
function TransitRow({ aspect }: { aspect: TransitAspect }) {
  const aspectColor = ASPECT_COLORS[aspect.aspect] ?? { bg: '#f9fafb', text: '#374151', border: '#e5e7eb' };
  const isLent = PLANETES_LENTES.includes(aspect.planeteTransit);

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '50px 130px 100px 150px 90px 60px',
      gap: '0.5rem',
      alignItems: 'center',
      padding: '6px 12px',
      background: '#fff',
      borderRadius: 4,
      borderLeft: `3px solid ${aspectColor.border}`,
      fontSize: '0.82rem',
      fontWeight: isLent ? 500 : 400,
    }}>
      {/* Heure */}
      <span style={{ color: colors.textMuted, fontSize: '0.75rem' }}>{aspect.heure}</span>

      {/* Planète transit */}
      <span>
        <span style={{ fontFamily: 'serif', marginRight: 4 }}>{PLANET_SYMBOLS[aspect.planeteTransit] ?? ''}</span>
        {aspect.planeteTransit}
      </span>

      {/* Aspect */}
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 8px', borderRadius: 12,
        background: aspectColor.bg, color: aspectColor.text,
        fontSize: '0.75rem', fontWeight: 500,
      }}>
        {ASPECT_SYMBOLS[aspect.aspect] ?? ''} {aspect.aspect}
      </span>

      {/* Point natal */}
      <span>
        <span style={{ fontFamily: 'serif', marginRight: 4 }}>{PLANET_SYMBOLS[aspect.pointNatal] ?? ''}</span>
        {aspect.pointNatal}
      </span>

      {/* Position */}
      <span style={{ color: colors.textMid, fontSize: '0.8rem' }}>
        {aspect.position} {aspect.signe.substring(0, 3)}
      </span>

      {/* Direction */}
      <span style={{ fontSize: '0.7rem', color: colors.textMuted }}>
        {aspect.direction === 'applicant' ? '→' : '←'} {aspect.vitesse != null ? `${aspect.vitesse}%` : ''}
      </span>
    </div>
  );
}

/** Contrôles de pagination */
function Pagination({ page, totalPages, onPageChange }: {
  page: number; totalPages: number; onPageChange: (p: number) => void;
}) {
  // Calculer les pages visibles (max 7 boutons)
  const pages: (number | '...')[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (page > 3) pages.push('...');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
      pages.push(i);
    }
    if (page < totalPages - 2) pages.push('...');
    pages.push(totalPages);
  }

  return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      gap: 4, marginTop: '1rem', paddingBottom: '1rem',
    }}>
      <button
        onClick={() => onPageChange(page - 1)}
        disabled={page === 1}
        style={{
          padding: '6px 10px', borderRadius: 6, border: `1px solid ${colors.border}`,
          background: '#fff', color: page === 1 ? colors.border : colors.text,
          cursor: page === 1 ? 'default' : 'pointer', fontSize: '0.8rem',
          fontFamily: 'inherit',
        }}
      >
        <i className="bi bi-chevron-left" />
      </button>
      {pages.map((p, i) =>
        p === '...' ? (
          <span key={`dot-${i}`} style={{ padding: '0 4px', color: colors.textMuted, fontSize: '0.8rem' }}>…</span>
        ) : (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            style={{
              padding: '6px 10px', borderRadius: 6, fontSize: '0.8rem',
              fontFamily: 'inherit', cursor: 'pointer', minWidth: 34,
              border: p === page ? '1px solid #0891b2' : `1px solid ${colors.border}`,
              background: p === page ? '#0891b2' : '#fff',
              color: p === page ? '#fff' : colors.text,
              fontWeight: p === page ? 600 : 400,
            }}
          >
            {p}
          </button>
        ),
      )}
      <button
        onClick={() => onPageChange(page + 1)}
        disabled={page === totalPages}
        style={{
          padding: '6px 10px', borderRadius: 6, border: `1px solid ${colors.border}`,
          background: '#fff', color: page === totalPages ? colors.border : colors.text,
          cursor: page === totalPages ? 'default' : 'pointer', fontSize: '0.8rem',
          fontFamily: 'inherit',
        }}
      >
        <i className="bi bi-chevron-right" />
      </button>
    </div>
  );
}

/** Vue regroupée par planète transitante */
function TransitByPlanete({ groups }: { groups: Record<string, TransitAspect[]> }) {
  const [openPlanetes, setOpenPlanetes] = useState<Set<string>>(new Set(PLANETES_LENTES));

  function togglePlanete(p: string) {
    const next = new Set(openPlanetes);
    if (next.has(p)) next.delete(p);
    else next.add(p);
    setOpenPlanetes(next);
  }

  // Trier : planètes lentes d'abord
  const sorted = Object.entries(groups).sort((a, b) => {
    const aLent = PLANETES_LENTES.includes(a[0]) ? 0 : 1;
    const bLent = PLANETES_LENTES.includes(b[0]) ? 0 : 1;
    if (aLent !== bLent) return aLent - bLent;
    return b[1].length - a[1].length;
  });

  return (
    <div style={{ display: 'grid', gap: '0.75rem' }}>
      {sorted.map(([planete, aspects]) => (
        <div key={planete} style={{
          background: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}>
          <button
            onClick={() => togglePlanete(planete)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0.75rem 1rem', border: 'none', background: 'none',
              cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.9rem', fontWeight: 600,
              color: colors.text,
            }}
          >
            <span>
              <span style={{ fontFamily: 'serif', marginRight: 6, fontSize: '1.1rem' }}>
                {PLANET_SYMBOLS[planete] ?? ''}
              </span>
              {planete}
              <span style={{
                marginLeft: 8, padding: '2px 8px', borderRadius: 12,
                background: colors.bgPage, fontSize: '0.75rem', fontWeight: 500, color: colors.textMid,
              }}>
                {aspects.length}
              </span>
            </span>
            <i className={`bi bi-chevron-${openPlanetes.has(planete) ? 'up' : 'down'}`}
               style={{ color: colors.textMuted }} />
          </button>

          {openPlanetes.has(planete) && (
            <div style={{ padding: '0 0.5rem 0.5rem' }}>
              {aspects.map((a, i) => (
                <TransitRow key={i} aspect={a} />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
